import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, Download, ChevronLeft, ChevronRight, FileText, Loader2, ExternalLink } from 'lucide-react'
import { chatService } from '../../services/chatService'
import { toast } from '../../stores/uiStore'

const isImg = (a) => (a?.content_type || '').startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/i.test(a?.file_name || '')
const isPdf = (a) => (a?.content_type || '') === 'application/pdf' || /\.pdf$/i.test(a?.file_name || '')
const isDocx = (a) => /wordprocessingml/i.test(a?.content_type || '') || /\.docx$/i.test(a?.file_name || '')
// Excel tarayıcıda çizilemez ve mammoth yalnız Word okur → sunucudan metni istenir
// (doc_text_service; mail eki çıkarımıyla ORTAK kod). Teklif/BOM listeleri sık sık
// .xlsx geldiği için indirmeden bakabilmek gerekiyor.
const isXlsx = (a) => /spreadsheetml|ms-excel/i.test(a?.content_type || '')
  || /\.(xlsx|xlsm)$/i.test(a?.file_name || '')
const isTxt = (a) => (a?.content_type || '').startsWith('text/')
  || /\.(txt|md|csv|log|json|xml|ya?ml|ini|sql)$/i.test(a?.file_name || '')
const fmtSize = (b) => (!b ? '' : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`)

/**
 * Ek önizleme — yeni sekme yerine sayfa üstünde popup.
 * Resim, PDF, Word (.docx / mammoth) gömülü; diğer türlerde indirme.
 */
/**
 * Excel önizlemesi — GERÇEK tablo. Düz metin (pipe ile birleşik satır) ekranda
 * okunmuyordu: sütunlar hizasız, başlık satırı ayırt edilemiyordu.
 * İlk satır başlık varsayılır (Excel dökümlerinde neredeyse her zaman öyledir);
 * yanlışsa yalnız görünüm etkilenir, veri değişmez.
 */
export function SheetView({ sheets, index, onPick }) {
  const sh = sheets[Math.min(index, sheets.length - 1)] || sheets[0]
  const rows = sh?.rows || []
  const head = rows[0] || []
  const body = rows.slice(1)
  const cols = rows.reduce((m, r) => Math.max(m, r.length), 0)
  return (
    <div onMouseDown={(e) => e.stopPropagation()}
      className="w-full max-w-6xl max-h-full flex flex-col bg-white rounded-lg overflow-hidden">
      {sheets.length > 1 && (
        <div className="flex items-center gap-1 px-3 pt-2 pb-1 border-b border-gray-100 overflow-x-auto shrink-0">
          {sheets.map((s, i) => (
            <button key={s.name + i} onClick={() => onPick(i)}
              className={`px-2.5 py-1 rounded-lg text-[12px] whitespace-nowrap ${
                i === index ? 'bg-violet-50 text-violet-700 font-medium' : 'text-gray-500 hover:bg-gray-50'}`}>
              {s.name}
            </button>
          ))}
        </div>
      )}
      <div className="flex-1 overflow-auto">
        <table className="border-collapse text-[12px] w-max min-w-full">
          <thead className="sticky top-0 z-[1]">
            <tr>
              <th className="sticky left-0 z-[2] bg-gray-100 border border-gray-200 px-2 py-1 text-[10px] text-gray-400 font-normal w-10" />
              {Array.from({ length: cols }).map((_, c) => (
                <th key={c} className="bg-violet-50 border border-gray-200 px-2.5 py-1.5 text-left font-semibold text-violet-700 whitespace-nowrap">
                  {head[c] || ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((r, i) => (
              <tr key={i} className={i % 2 ? 'bg-gray-50/60' : ''}>
                {/* Satır numarası: uzun listede "kaçıncı satırdayım" bilgisi kaybolmasın */}
                <td className="sticky left-0 bg-gray-50 border border-gray-200 px-2 py-1 text-[10px] text-gray-400 text-right">
                  {i + 2}
                </td>
                {Array.from({ length: cols }).map((_, c) => (
                  <td key={c} className="border border-gray-200 px-2.5 py-1 text-gray-800 align-top max-w-[380px]">
                    {r[c] || ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-3 py-1.5 border-t border-gray-100 text-[11px] text-gray-500 shrink-0">
        {rows.length} satır · {cols} sütun
        {sh?.truncated ? ' · liste kırpıldı, tamamı için indirin' : ''}
        <span className="text-gray-400"> · hücre biçimi ve formüller gösterilmez</span>
      </div>
    </div>
  )
}

export default function AttachmentPreview({ items = [], index = 0, company, onClose }) {
  const [i, setI] = useState(index)
  const [url, setUrl] = useState(null)
  const [text, setText] = useState(null)
  const [docxHtml, setDocxHtml] = useState(null)
  const [sheets, setSheets] = useState(null)     // Excel: [{name, rows, truncated}]
  const [sheetIx, setSheetIx] = useState(0)
  const [loading, setLoading] = useState(true)
  const att = items[i]
  const many = items.length > 1

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose?.() }
      else if (e.key === 'ArrowRight' && many) setI((p) => (p + 1) % items.length)
      else if (e.key === 'ArrowLeft' && many) setI((p) => (p - 1 + items.length) % items.length)
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [onClose, many, items.length])

  useEffect(() => {
    if (!att) return
    let alive = true
    let made = null
    setLoading(true); setUrl(null); setText(null); setDocxHtml(null); setSheets(null); setSheetIx(0)
    chatService.fetchAttachmentBlob(company, att.id)
      .then(async (b) => {
        if (!alive) return
        made = URL.createObjectURL(b)
        setUrl(made)
        if (isTxt(att) && b.size <= 512 * 1024) {
          try { const t = await b.text(); if (alive) setText(t) } catch { /* yut */ }
        }
        if (isXlsx(att)) {
          // Sunucu sayfa/satır/hücre yapısını döner → gerçek tablo çizilir.
          // Yapı gelmezse düz metne düşülür; o da yoksa indirme kartına.
          try {
            const r = await chatService.attachmentText(company, att.id)
            const sh = r?.data?.sheets || []
            const t = r?.data?.text || ''
            if (alive && sh.length) setSheets(sh)
            else if (alive && t.trim()) setText(t)
          } catch { /* yut */ }
        }
        if (isDocx(att) && b.size <= 15 * 1024 * 1024) {
          try {
            const mod = await import('mammoth')
            const mammoth = mod.default || mod
            const ab = await b.arrayBuffer()
            const r = await mammoth.convertToHtml({ arrayBuffer: ab })
            if (alive) setDocxHtml(r.value || '<p>(boş belge)</p>')
          } catch {
            if (alive) setDocxHtml(null)
          }
        }
        if (alive) setLoading(false)
      })
      .catch((e) => {
        if (!alive) return
        setLoading(false)
        toast.error('Dosya açılamadı', e.response?.data?.detail || e.message)
      })
    return () => { alive = false; if (made) URL.revokeObjectURL(made) }
  }, [company, att?.id])

  if (!att) return null

  const download = () => {
    if (!url) return
    const a = document.createElement('a')
    a.href = url; a.download = att.file_name || 'dosya'
    document.body.appendChild(a); a.click(); a.remove()
  }

  return createPortal(
    <div className="fixed inset-0 z-[95] flex flex-col" onMouseDown={onClose}>
      <div className="absolute inset-0 bg-black/70" />

      <div className="relative flex items-center gap-3 px-4 py-2.5 text-white" onMouseDown={(e) => e.stopPropagation()}>
        <span className="text-sm font-medium truncate flex-1">{att.file_name}</span>
        {att.size_bytes ? <span className="text-[11px] text-white/60 shrink-0">{fmtSize(att.size_bytes)}</span> : null}
        {many && <span className="text-[11px] text-white/60 shrink-0">{i + 1}/{items.length}</span>}
        <button onClick={download} title="İndir" className="p-1.5 rounded-lg hover:bg-white/15"><Download size={16} /></button>
        {url && (
          <a href={url} target="_blank" rel="noreferrer" title="Yeni sekmede aç" className="p-1.5 rounded-lg hover:bg-white/15">
            <ExternalLink size={16} />
          </a>
        )}
        <button onClick={onClose} title="Kapat (Esc)" className="p-1.5 rounded-lg hover:bg-white/15"><X size={18} /></button>
      </div>

      <div className="relative flex-1 flex items-center justify-center px-4 pb-4 min-h-0">
        {many && (
          <button onMouseDown={(e) => { e.stopPropagation(); setI((p) => (p - 1 + items.length) % items.length) }}
            className="absolute left-2 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"><ChevronLeft size={20} /></button>
        )}

        {loading ? (
          <Loader2 size={26} className="animate-spin text-white/70" />
        ) : !url ? (
          <div className="text-white/70 text-sm">Dosya yüklenemedi.</div>
        ) : isImg(att) ? (
          <img src={url} alt={att.file_name} onMouseDown={(e) => e.stopPropagation()}
            className="max-h-full max-w-full object-contain rounded-lg bg-white" />
        ) : sheets ? (
          <SheetView sheets={sheets} index={sheetIx} onPick={setSheetIx} />
        ) : text !== null ? (
          <div onMouseDown={(e) => e.stopPropagation()}
            className="w-full max-w-4xl max-h-full overflow-auto bg-white rounded-lg">
            <pre className="p-4 text-[12.5px] leading-relaxed text-gray-800 whitespace-pre-wrap break-words">
              {text}
            </pre>
          </div>
        ) : isPdf(att) ? (
          <iframe title={att.file_name} src={url} onMouseDown={(e) => e.stopPropagation()}
            className="w-full h-full max-w-5xl rounded-lg bg-white" />
        ) : docxHtml ? (
          <div onMouseDown={(e) => e.stopPropagation()}
            className="docx-preview w-full max-w-4xl max-h-full overflow-auto bg-white rounded-lg p-7"
            dangerouslySetInnerHTML={{ __html: docxHtml }} />
        ) : (
          <div onMouseDown={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl px-8 py-10 text-center max-w-sm">
            <FileText size={36} className="mx-auto text-gray-300 mb-3" />
            <div className="text-sm font-medium text-gray-800 truncate">{att.file_name}</div>
            <p className="text-[12px] text-gray-400 mt-1">Bu dosya türü tarayıcıda önizlenemiyor.</p>
            <button onClick={download}
              className="mt-4 px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg inline-flex items-center gap-1.5">
              <Download size={14} /> İndir
            </button>
          </div>
        )}

        {many && (
          <button onMouseDown={(e) => { e.stopPropagation(); setI((p) => (p + 1) % items.length) }}
            className="absolute right-2 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"><ChevronRight size={20} /></button>
        )}
      </div>
    </div>,
    document.body,
  )
}
